import { create } from 'zustand';
import { defaultPUR5020_R01HeaderDto, PUR5020_R01HeaderDto, PUR5020_R01DetailDto } from 'src/containers/mes/PUR/PUR5020_R01/Dto_PUR5020_R01';
import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';

export interface PUR5020_R01Filter {
	compCd: string;
	txDt: DateRange | undefined;
	logicCenter: string;
	warehouseCd: string;
	brandCd: string;
	partnerCd: string;
	partnerNm: string;
	itemCd: string;
	itemNm: string;
	itemGroup: string;
	itemStatus: string;
	startDate: string;
	endDate: string;
	searchText: string;
	includeZeroStock: boolean;
}

export interface SelectedItemInfo {
	itemCd: string;
	itemNm: string;
}

export interface SelectedRowInfo {
	itemCd: string;
	warehouseCd: string;
	logicCenter: string;
	itemStatus: string;
	valueIndex: number;
}

export interface Store_PUR5020_R01 {
	PUR5020_R01Filter: PUR5020_R01Filter;
	PUR5020_R01PastFilter: PUR5020_R01Filter;
	setPUR5020_R01Filter: (filter: Partial<PUR5020_R01Filter>) => void;
	setPUR5020_R01PastFilter: (filter: Partial<PUR5020_R01Filter>) => void;

	PUR5020_R01List: PUR5020_R01HeaderDto[];
	setPUR5020_R01List: (list: PUR5020_R01HeaderDto[]) => void;

	PUR5020_R01DetailList: PUR5020_R01DetailDto[];
	setPUR5020_R01DetailList: (list: PUR5020_R01DetailDto[]) => void;

	deletedPUR5020_R01DetailList: string[];
	setDeletedPUR5020_R01DetailList: (idList: string[]) => void;
	addDeletedPUR5020_R01DetailList: (idList: string[]) => void;
	removeDeletedPUR5020_R01DetailList: (idList?: string[]) => void;

	selectedPUR5020_R01DetailList: string | undefined;
	setSelectedPUR5020_R01DetailList: (id: string | undefined) => void;
	initSelectedPUR5020_R01DetailList: () => void;

	originalPUR5020_R01DetailList: PUR5020_R01DetailDto[];
	setOriginalPUR5020_R01DetailList: (data: PUR5020_R01DetailDto[]) => void;

	selectedPUR5020_R01: string | undefined;
	setSelectedPUR5020_R01: (itemCd: string | undefined) => void;
	initSelectedPUR5020_R01: () => void;

	selectedRowInfo: SelectedRowInfo | undefined;
	setSelectedRowInfo: (rowInfo: SelectedRowInfo | undefined) => void;

	selectedItemInfo: SelectedItemInfo | undefined;
	setSelectedItemInfo: (itemInfo: SelectedItemInfo | undefined) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	initData: () => void;
}

export const defaultPUR5020_R01Filter: PUR5020_R01Filter = {
	compCd: '',
	txDt: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date()
	},
	logicCenter: '',
	warehouseCd: '',
	brandCd: '',
	partnerCd: '',
	partnerNm: '',
	itemCd: '',
	itemNm: '',
	itemGroup: '',
	itemStatus: '',
	startDate: '',
	endDate: '',
	searchText: '',
	includeZeroStock: false,
};

export const usePUR5020_R01Store = create<Store_PUR5020_R01>((set, get) => ({
	PUR5020_R01Filter: defaultPUR5020_R01Filter,
	PUR5020_R01PastFilter: defaultPUR5020_R01Filter,
	setPUR5020_R01Filter: (filter) => set((state) => ({ PUR5020_R01Filter: { ...state.PUR5020_R01Filter, ...filter } })),
	setPUR5020_R01PastFilter: (filter) => set((state) => ({ PUR5020_R01PastFilter: { ...state.PUR5020_R01PastFilter, ...filter } })),

	PUR5020_R01List: [],
	setPUR5020_R01List: (list: PUR5020_R01HeaderDto[]) => set((state) => {
		let newList: PUR5020_R01HeaderDto[] = [];
		let valueAndOrderIndex = 1;
		list.forEach((item) => {
			newList.push({
				...defaultPUR5020_R01HeaderDto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return { ...state, PUR5020_R01List: newList };
	}),

	PUR5020_R01DetailList: [],
	setPUR5020_R01DetailList: (list: PUR5020_R01DetailDto[]) => set({ PUR5020_R01DetailList: list }),

	deletedPUR5020_R01DetailList: [],
	setDeletedPUR5020_R01DetailList: (idList: string[]) => set({ deletedPUR5020_R01DetailList: idList }),
	addDeletedPUR5020_R01DetailList: (idList: string[]) => set((state) => ({
		deletedPUR5020_R01DetailList: [...state.deletedPUR5020_R01DetailList, ...idList]
	})),
	removeDeletedPUR5020_R01DetailList: (idList?: string[]) => set((state) => ({
		deletedPUR5020_R01DetailList: idList ? state.deletedPUR5020_R01DetailList.filter(v => !idList.includes(v)) : []
	})),

	selectedPUR5020_R01DetailList: undefined,
	setSelectedPUR5020_R01DetailList: (id: string | undefined) => set({ selectedPUR5020_R01DetailList: id }),
	initSelectedPUR5020_R01DetailList: () => set({ selectedPUR5020_R01DetailList: undefined }),

	originalPUR5020_R01DetailList: [],
	setOriginalPUR5020_R01DetailList: (data: PUR5020_R01DetailDto[]) => set(() => ({ originalPUR5020_R01DetailList: data })),

	selectedPUR5020_R01: undefined,
	setSelectedPUR5020_R01: (itemCd: string | undefined) => set((state) => ({
		...state,
		selectedPUR5020_R01: itemCd,
	})),
	initSelectedPUR5020_R01: () => set({ selectedPUR5020_R01: undefined }),

	selectedRowInfo: undefined,
	setSelectedRowInfo: (rowInfo: SelectedRowInfo | undefined) => set({ selectedRowInfo: rowInfo }),

	selectedItemInfo: undefined,
	setSelectedItemInfo: (itemInfo: SelectedItemInfo | undefined) => set({ selectedItemInfo: itemInfo }),

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state) => set({ detailColumnState: state }),

	allCount: 0,
	setAllCount: (allCount: number) => set({ allCount }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	initData: () => set({
		PUR5020_R01Filter: defaultPUR5020_R01Filter,
		PUR5020_R01PastFilter: defaultPUR5020_R01Filter,
		PUR5020_R01List: [],
		PUR5020_R01DetailList: [],
		deletedPUR5020_R01DetailList: [],
		selectedPUR5020_R01DetailList: undefined,
		originalPUR5020_R01DetailList: [],
		selectedPUR5020_R01: undefined,
		selectedRowInfo: undefined,
		selectedItemInfo: undefined,
		columnState: null,
		detailColumnState: null,
		allCount: 0,
		scrollTop: null,
		detailScrollTop: null,
	}),
}));

export default usePUR5020_R01Store;
